/** moderator/ontology — HOOKS. Every read is scoped to a single match. */
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ontologyApi } from './api';
import { ontologyKeys } from './keys';

export function useOntologyStatus(matchId: string | null) {
  return useQuery({
    queryKey: ontologyKeys.status(matchId),
    queryFn: () => ontologyApi.status(matchId!),
    enabled: Boolean(matchId),
    retry: false,
  });
}

export function useOntologyDecisions(matchId: string | null, cohortId: string | null) {
  return useQuery({
    queryKey: ontologyKeys.decisions(matchId, cohortId),
    queryFn: () => ontologyApi.decisions(matchId!, cohortId!),
    enabled: Boolean(matchId && cohortId),
    retry: false,
  });
}

/**
 * Fetching a decision packet is not a pure read: the server imports any
 * transcript that landed after the match, which can close the session's
 * transcript status and flip the audit from INCOMPLETE to COMPLETE.
 *
 * Invalidating the status here ties that change to the fetch that caused it.
 * Without it the badge kept showing the pre-import state and then changed on
 * some unrelated later refetch, which reads as the screen updating on its own.
 */
export function useOntologyAction(matchId: string | null, actionId: string | null) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ontologyKeys.action(matchId, actionId),
    queryFn: () => ontologyApi.action(matchId!, actionId!),
    enabled: Boolean(matchId && actionId),
    retry: false,
  });

  const fetchedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (!fetchedAt || !matchId) return;
    void queryClient.invalidateQueries({ queryKey: ontologyKeys.status(matchId) });
  }, [fetchedAt, matchId, queryClient]);

  return query;
}

export function useOntologyEvaluations(matchId: string | null, cohortId: string | null) {
  return useQuery({
    queryKey: ontologyKeys.evaluations(matchId, cohortId),
    queryFn: () => ontologyApi.evaluations(matchId!, cohortId!),
    enabled: Boolean(matchId && cohortId),
    retry: false,
  });
}

/**
 * Publishing flips a draft to PUBLISHED and supersedes prior versions, so the
 * whole ontology tree for the match is invalidated rather than one list.
 */
export function useUpdateOntologyEvaluation(matchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      evaluationId: string;
      changes: {
        processAssessment?: string;
        outcomeAssessment?: string;
        narrative?: string;
      };
    }) => ontologyApi.updateEvaluation(matchId, input.evaluationId, input.changes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ontologyKeys.all() }),
  });
}

export function usePublishOntologyEvaluation(matchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (evaluationId: string) => ontologyApi.publishEvaluation(matchId, evaluationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ontologyKeys.all() }),
  });
}
